import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, IconButton, CircularProgress,
  Grid, Chip, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tooltip, Avatar, Divider, Alert, Button,
  Paper, Dialog, DialogTitle, DialogContent, DialogActions,
  Snackbar, TextField, MenuItem, InputAdornment, TablePagination,
} from '@mui/material';
import {
  ArrowBack, Edit, Phone, LocationOn, Delete, PhotoCamera,
  Timeline, BarChart, Close, Refresh, UploadFile, Call,
  Contacts as ContactsIcon, PhotoLibrary, DirectionsCar, Search,
  Payment, NotificationsActive, Add, CheckCircle,
  Assignment, AssignmentReturn,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { InvokeApi, postImg, apiList } from '../services/ApiService';
import { Config } from '../config/Config';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const InfoRow = ({ label, value }) => (
  <Box sx={{ py: 1, borderBottom: '1px solid #f5f5f5' }}>
    <Typography fontSize={11} color="text.secondary" fontWeight={500}>{label}</Typography>
    <Typography fontSize={13} fontWeight={600} color={value && value !== '—' ? '#1a1a1a' : 'text.disabled'}>{value || '—'}</Typography>
  </Box>
);

const UserDetail = () => {
  const { userId } = useParams();
  const navigate = useNavigate();

  // Core state
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('User');
  const [user, setUser] = useState(null);
  const [gpsData, setGpsData] = useState(null);

  // Image upload state
  const [uploadingImage, setUploadingImage] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });

  // Edit form state
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Per-tab data
  const [callRecords, setCallRecords] = useState([]);
  const [callsLoading, setCallsLoading] = useState(false);
  const [callSearch, setCallSearch] = useState('');
  const [callPage, setCallPage] = useState(0);
  const [callPageSize, setCallPageSize] = useState(10);

  // History state
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Contacts state
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactSearch, setContactSearch] = useState('');
  const [contactPage, setContactPage] = useState(0);
  const [contactPageSize, setContactPageSize] = useState(10);

  // Photos state
  const [photos, setPhotos] = useState([]);
  const [photosLoading, setPhotosLoading] = useState(false);

  // GPS Map dialog
  const [mapDialog, setMapDialog] = useState(false);

  // Vehicle management state
  const [returnDialog, setReturnDialog] = useState(false);
  const [returnNotes, setReturnNotes] = useState('');
  const [returningVehicle, setReturningVehicle] = useState(false);
  
  const [changeDialog, setChangeDialog] = useState(false);
  const [availableVehicles, setAvailableVehicles] = useState([]);

  // Payment state
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [collections, setCollections] = useState({});   // keyed by planId
  const [expandedPlan, setExpandedPlan] = useState(null);

  // Plan dialog
  const EMPTY_PLAN = { frequency: 'monthly', amount: '', advance_paid: '', start_date: new Date().toISOString().slice(0,10), notes: '' };
  const [planDialog, setPlanDialog] = useState(false);
  const [editPlan, setEditPlan] = useState(null);
  const [planForm, setPlanForm] = useState(EMPTY_PLAN);
  const [savingPlan, setSavingPlan] = useState(false);

  // Collection dialog
  const EMPTY_COL = { amount: '', paid_date: new Date().toISOString().slice(0,10), due_date: '', method: 'cash', status: 'paid', transaction_type: 'rent', notes: '' };
  const [colDialog, setColDialog] = useState(false);
  const [colPlanId, setColPlanId] = useState(null);
  const [editCol, setEditCol] = useState(null);
  const [colForm, setColForm] = useState(EMPTY_COL);
  const [savingCol, setSavingCol] = useState(false);

  // Reminder dialog
  const [reminderDialog, setReminderDialog] = useState(false);
  const [reminderPlan, setReminderPlan] = useState(null);
  const [reminderMsg, setReminderMsg] = useState('');
  const [sendingReminder, setSendingReminder] = useState(false);
  const [selectedNewVehicle, setSelectedNewVehicle] = useState('');
  const [changingVehicle, setChangingVehicle] = useState(false);

  useEffect(() => {
    loadUserData();
  }, [userId]);

  const loadUserData = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      // Fetch user details
      const uRes = await InvokeApi(Config.base_url + apiList.getUser, { id: userId });
      if (uRes?.code === '200' && uRes.user) {
        const u = uRes.user;
        setUser(u);
        // Initialize form with user data
        setForm({
          id: u.id,
          name: u.name || '',
          phone: u.phone || '',
          email: u.email || '',
          address: u.address || '',
          kyc_status: u.kyc_status || 'pending',
          is_active: u.is_active !== undefined ? u.is_active : 1,
        });
      }

      // Fetch GPS data (using user ID as device_id)
      const gRes = await InvokeApi(Config.base_url + '/getDeviceLocations', { device_id: String(userId) });
      if (gRes?.code === '200' && gRes.location) {
        setGpsData(gRes.location);
      }
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load call logs when Call Logs tab is active (combines both admin and device calls)
  useEffect(() => {
    if (activeTab !== 'CallLogs') return;
    const loadCalls = async () => {
      setCallsLoading(true);
      // Get device call logs
      const data = await InvokeApi(Config.base_url + '/getDeviceCallLogs', { device_id: String(userId) });
      setCallRecords(data?.call_logs || []);
      setCallsLoading(false);
    };
    loadCalls();
  }, [activeTab, userId]);

  // Load assignment history when History tab is active
  useEffect(() => {
    if (activeTab !== 'History') return;
    const loadHistory = async () => {
      setHistoryLoading(true);
      const data = await InvokeApi(Config.base_url + apiList.getUserHistory, { id: userId });
      setHistory(data?.history || []);
      setHistoryLoading(false);
    };
    loadHistory();
  }, [activeTab, userId]);

  // Load contacts when Contacts tab is active
  useEffect(() => {
    if (activeTab !== 'Contacts') return;
    const loadContacts = async () => {
      setContactsLoading(true);
      const data = await InvokeApi(Config.base_url + '/getDeviceContacts', { device_id: String(userId) });
      setContacts(data?.contacts || []);
      setContactsLoading(false);
    };
    loadContacts();
  }, [activeTab, userId]);

  // Load photos when Photos tab is active
  useEffect(() => {
    if (activeTab !== 'Photos') return;
    const loadPhotos = async () => {
      setPhotosLoading(true);
      const data = await InvokeApi(Config.base_url + '/getDevicePhotos', { device_id: String(userId) });
      setPhotos(data?.photos || []);
      setPhotosLoading(false);
    };
    loadPhotos();
  }, [activeTab, userId]);

  // Load payment plans when Payments tab is active
  useEffect(() => {
    if (activeTab !== 'Payments') return;
    loadPlans();
  }, [activeTab, userId]);

  const loadPlans = async () => {
    setPlansLoading(true);
    const res = await InvokeApi(Config.base_url + apiList.getPaymentPlans, { user_id: userId });
    setPlans(res?.plans ?? []);
    setPlansLoading(false);
  };

  const loadCollections = async (planId) => {
    const res = await InvokeApi(Config.base_url + apiList.getPaymentCollections, { plan_id: planId });
    setCollections(prev => ({ ...prev, [planId]: res?.collections ?? [] }));
  };

  const togglePlan = async (planId) => {
    if (expandedPlan === planId) { setExpandedPlan(null); return; }
    setExpandedPlan(planId);
    if (!collections[planId]) await loadCollections(planId);
  };

  // Plan handlers
  const openAddPlan  = () => {
    setEditPlan(null);
    setPlanForm({ ...EMPTY_PLAN, vehicle_id: user?.vehicle?.vehicle_id ?? '' });
    setPlanDialog(true);
  };
  const openEditPlan = (p) => {
    setEditPlan(p);
    setPlanForm({
      frequency:    p.frequency,
      amount:       p.amount,
      advance_paid: p.advance_paid,
      start_date:   p.start_date,
      notes:        p.notes || '',
      vehicle_id:   p.vehicle_id ?? user?.vehicle?.vehicle_id ?? '',
    });
    setPlanDialog(true);
  };

  const handleSavePlan = async () => {
    // if (!planForm.amount || parseFloat(planForm.amount) <= 0) {
    //   setSnack({ open: true, msg: 'Amount is required', sev: 'warning' }); return;
    // }
    setSavingPlan(true);
    const endpoint = editPlan ? apiList.editPaymentPlan : apiList.addPaymentPlan;
    const payload  = editPlan
      ? { id: editPlan.id, ...planForm }
      : { user_id: userId, ...planForm };
    const res = await InvokeApi(Config.base_url + endpoint, payload);
    setSavingPlan(false);
    if (res?.code === '200') {
      setSnack({ open: true, msg: editPlan ? 'Plan updated' : 'Plan created', sev: 'success' });
      setPlanDialog(false);
      loadPlans();
    } else {
      setSnack({ open: true, msg: res?.message || 'Failed', sev: 'error' });
    }
  };

  // Collection handlers
  const openAddCol  = (planId) => { setColPlanId(planId); setEditCol(null); setColForm(EMPTY_COL); setColDialog(true); };
  const openEditCol = (planId, col) => {
    setColPlanId(planId);
    setEditCol(col);
    setColForm({ amount: col.amount, paid_date: col.paid_date, due_date: col.due_date || '', method: col.method, status: col.status, transaction_type: col.transaction_type || 'rent', notes: col.notes || '' });
    setColDialog(true);
  };

  const handleSaveCol = async () => {
    // if (!colForm.amount || parseFloat(colForm.amount) <= 0) {
    //   setSnack({ open: true, msg: 'Amount is required', sev: 'warning' }); return;
    // }
    setSavingCol(true);
    const endpoint = editCol ? apiList.editPaymentCollection : apiList.addPaymentCollection;
    const payload  = editCol
      ? { id: editCol.id, ...colForm }
      : { plan_id: colPlanId, user_id: userId, ...colForm };
    const res = await InvokeApi(Config.base_url + endpoint, payload);
    setSavingCol(false);
    if (res?.code === '200') {
      setSnack({ open: true, msg: editCol ? 'Collection updated' : 'Payment recorded', sev: 'success' });
      setColDialog(false);
      loadCollections(colPlanId);
      loadPlans(); // refresh totals
    } else {
      setSnack({ open: true, msg: res?.message || 'Failed', sev: 'error' });
    }
  };

  // Reminder handler
  const openReminder = (plan) => { setReminderPlan(plan); setReminderMsg(''); setReminderDialog(true); };
  const handleSendReminder = async () => {
    setSendingReminder(true);
    const res = await InvokeApi(Config.base_url + apiList.sendPaymentReminder, {
      user_id: userId,
      plan_id: reminderPlan?.id,
      message: reminderMsg.trim() || undefined,
    });
    setSendingReminder(false);
    if (res?.code === '200') {
      setSnack({ open: true, msg: 'Reminder sent successfully', sev: 'success' });
      setReminderDialog(false);
    } else {
      setSnack({ open: true, msg: res?.message || 'Failed to send reminder', sev: 'error' });
    }
  };

  const STATUS_COLOR = { paid: 'success', pending: 'warning', partial: 'info', overdue: 'error' };
  const METHOD_LABEL = { cash: '💵 Cash', upi: '📱 UPI', bank: '🏦 Bank', other: '📋 Other' };
  const FREQ_LABEL   = { daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly' };

  // Handle image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('id', userId);
      
      const res = await postImg(Config.base_url + apiList.uploadUserImage, formData);
      if (res?.code === '200') {
        setSnack({ open: true, msg: 'Image uploaded successfully', sev: 'success' });
        await loadUserData();
      } else {
        setSnack({ open: true, msg: res?.message || 'Upload failed', sev: 'error' });
      }
    } catch (err) {
      setSnack({ open: true, msg: 'Upload error: ' + err.message, sev: 'error' });
    } finally {
      setUploadingImage(false);
    }
  };

  // Edit form handlers
  const handleSave = async () => {
    if (!form.name?.trim()) {
      setSnack({ open: true, msg: 'Name is required', sev: 'error' });
      return;
    }
    if (!form.phone?.trim()) {
      setSnack({ open: true, msg: 'Phone is required', sev: 'error' });
      return;
    }

    setSaving(true);
    const data = await InvokeApi(Config.base_url + apiList.editUser, form);
    setSaving(false);
    
    if (data?.code === '200') {
      setSnack({ open: true, msg: 'User updated successfully', sev: 'success' });
      await loadUserData();
      setActiveTab('User');
    } else {
      setSnack({ open: true, msg: data?.message || 'Failed to update', sev: 'error' });
    }
  };

  const f = (key) => (e) => setForm(prev => ({ ...prev, [key]: e.target.value }));

  const handleDelete = async () => {
    if (!window.confirm('Delete this user?')) return;
    const data = await InvokeApi(Config.base_url + apiList.deleteUser, { id: userId });
    if (data?.code === '200') navigate('/users');
    else alert(data?.message || 'Failed to delete');
  };

  // Open GPS Map Dialog
  const openMapDialog = () => {
    setMapDialog(true);
  };

  // Return Vehicle Handler
  const handleReturnVehicle = async () => {
    if (!user.vehicle?.vehicle_id) return;
    
    setReturningVehicle(true);
    const data = await InvokeApi(Config.base_url + '/admin/vehicle/return', {
      user_id: parseInt(userId),
      vehicle_id: user.vehicle.vehicle_id,
      notes: returnNotes.trim() || undefined,
    });
    setReturningVehicle(false);

    if (data?.code === '200') {
      setSnack({ open: true, msg: 'Vehicle returned successfully', sev: 'success' });
      setReturnDialog(false);
      setReturnNotes('');
      await loadUserData();
    } else {
      setSnack({ open: true, msg: data?.message || 'Failed to return vehicle', sev: 'error' });
    }
  };

  // Open Change Vehicle Dialog
  const openChangeVehicleDialog = async () => {
    setChangeDialog(true);
    // Fetch available vehicles
    const res = await InvokeApi(Config.base_url + apiList.getUnassignedVehicles, {});
    if (res?.code === '200') {
      setAvailableVehicles(res.vehicles || []);
    }
  };

  // Change Vehicle Handler
  const handleChangeVehicle = async () => {
    if (!selectedNewVehicle) {
      setSnack({ open: true, msg: 'Please select a vehicle', sev: 'error' });
      return;
    }
    if (!user.vehicle?.vehicle_id) return;

    setChangingVehicle(true);
    
    // Step 1: Return current vehicle
    const returnRes = await InvokeApi(Config.base_url + '/admin/vehicle/return', {
      user_id: parseInt(userId),
      vehicle_id: user.vehicle.vehicle_id,
      notes: 'Vehicle changed',
    });

    if (returnRes?.code !== '200') {
      setChangingVehicle(false);
      setSnack({ open: true, msg: 'Failed to return current vehicle', sev: 'error' });
      return;
    }

    // Step 2: Assign new vehicle
    const assignRes = await InvokeApi(Config.base_url + apiList.assignVehicle, {
      user_id: parseInt(userId),
      vehicle_id: parseInt(selectedNewVehicle),
    });

    setChangingVehicle(false);

    if (assignRes?.code === '200' || assignRes?.status === 'success') {
      setSnack({ open: true, msg: 'Vehicle changed successfully', sev: 'success' });
      setChangeDialog(false);
      setSelectedNewVehicle('');
      await loadUserData();
    } else {
      setSnack({ open: true, msg: assignRes?.message || 'Failed to assign new vehicle', sev: 'error' });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress sx={{ color: '#1a237e' }} />
      </Box>
    );
  }

  if (!user) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography color="text.secondary">User not found</Typography>
        <Button onClick={() => navigate('/users')} sx={{ mt: 2 }}>Back</Button>
      </Box>
    );
  }

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const kycStatusColor = {
    pending: { bg: '#fff3e0', color: '#e65100' },
    approved: { bg: '#e8f5e9', color: '#2e7d32' },
    rejected: { bg: '#ffebee', color: '#c62828' },
    review: { bg: '#e3f2fd', color: '#0277bd' },
  };

  const TABS = [
    { id: 'User',     label: 'User Information' },
    { id: 'History',  label: 'Vehicle History' },
    { id: 'CallLogs', label: 'Call Logs' },
    { id: 'Contacts', label: 'Contacts' },
    { id: 'Photos',   label: 'Photos' },
    { id: 'Payments', label: 'Payments' },
    { id: 'Edit',     label: 'Edit' },
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Tooltip title="Back">
          <IconButton onClick={() => navigate('/users')} sx={{ border: '1px solid #e8eaf6', borderRadius: 2 }}>
            <ArrowBack sx={{ color: '#1a237e' }} />
          </IconButton>
        </Tooltip>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={800} color="#1a237e">{user.name}</Typography>
          <Typography variant="body2" color="text.secondary">
            ID: #{user.id} · {user.phone}
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={3}>
        
        {/* Left Sidebar (3 cols) - Sticky */}
        <Grid item xs={12} md={3}>
          <Card sx={{ borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', overflow: 'hidden', position: 'sticky', top: 80 }}>
            {/* Header gradient */}
            <Box sx={{ height: 70, background: 'linear-gradient(135deg,#1a237e,#1565c0)' }} />
            
            <CardContent sx={{ pt: 0, textAlign: 'center', pb: 3 }}>
              {/* User Image with upload button */}
              <Box sx={{ position: 'relative', display: 'inline-block', mt: -5 }}>
                <Avatar
                  src={user.photo_url ? `${user.photo_url}` : undefined}
                  sx={{
                    width: 80,
                    height: 80,
                    border: '3px solid #fff',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                    background: 'linear-gradient(135deg,#1a237e,#1565c0)',
                    fontSize: 28,
                    fontWeight: 900,
                    color: '#fff',
                  }}
                >
                  {!user.photo_url && (user.name?.[0]?.toUpperCase() || 'U')}
                </Avatar>
                
                <label htmlFor="user-image-upload">
                  <input
                    id="user-image-upload"
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleImageUpload}
                  />
                  <Tooltip title="Change User Photo">
                    <IconButton
                      component="span"
                      size="small"
                      disabled={uploadingImage}
                      sx={{
                        position: 'absolute',
                        bottom: -2,
                        right: -2,
                        background: '#1a237e',
                        color: '#fff',
                        width: 26,
                        height: 26,
                        '&:hover': { background: '#1565c0' },
                      }}
                    >
                      {uploadingImage ? <CircularProgress size={12} color="inherit" /> : <PhotoCamera sx={{ fontSize: 14 }} />}
                    </IconButton>
                  </Tooltip>
                </label>
              </Box>

              <Typography fontWeight={800} fontSize={15} mt={1.5}>{user.name}</Typography>
              <Typography fontSize={12} color="text.secondary">{user.phone}</Typography>

              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
                <Chip label={`#${user.id}`} size="small" sx={{ background: '#e8eaf6', color: '#1a237e', fontWeight: 700, fontSize: 10 }} />
                <Chip
                  label={user.kyc_status?.toUpperCase() || 'PENDING'}
                  size="small"
                  sx={{
                    background: kycStatusColor[user.kyc_status]?.bg || '#fff3e0',
                    color: kycStatusColor[user.kyc_status]?.color || '#e65100',
                    fontWeight: 600,
                    fontSize: 10,
                  }}
                />
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Quick Info Icons */}
              <Box sx={{ textAlign: 'left', px: 1 }}>
                {[
                  { icon: <Phone fontSize="small" />, value: user.phone },
                  { icon: <LocationOn fontSize="small" />, value: user.address },
                ].map((r, i) => r.value ? (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.6 }}>
                    <Box sx={{ color: '#1a237e', flexShrink: 0 }}>{r.icon}</Box>
                    <Typography fontSize={12} color="text.secondary" sx={{ wordBreak: 'break-all' }}>{r.value}</Typography>
                  </Box>
                ) : null)}
              </Box>

              {/* Assigned Vehicle Info */}
              {user.vehicle && user.vehicle.vehicle_number && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box 
                    onClick={() => navigate(`/vehicle/${user.vehicle.vehicle_id}`)}
                    sx={{ 
                      p: 2, 
                      background: 'linear-gradient(135deg,#e8f5e9,#c8e6c9)', 
                      borderRadius: 2,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        background: 'linear-gradient(135deg,#c8e6c9,#a5d6a7)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(76,175,80,0.3)'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Avatar 
                        sx={{ 
                          width: 32, 
                          height: 32, 
                          bgcolor: '#2e7d32', 
                          fontSize: 14, 
                          fontWeight: 700 
                        }}
                      >
                        {user.vehicle.vehicle_number[0]}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#2e7d32', fontSize: 9, textTransform: 'uppercase' }}>
                          ASSIGNED VEHICLE
                        </Typography>
                        <Typography variant="body2" fontWeight={800} color="#2e7d32" fontSize={13}>
                          {user.vehicle.vehicle_number}
                        </Typography>
                      </Box>
                      <ArrowBack sx={{ fontSize: 16, color: '#2e7d32', transform: 'rotate(180deg)' }} />
                    </Box>
                    <Typography variant="caption" color="text.secondary" fontSize={10}>
                      Assigned: {formatDate(user.vehicle.assigned_date)}
                    </Typography>
                  </Box>

                  {/* Vehicle Action Buttons */}
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <Button
                      fullWidth
                      size="small"
                      variant="outlined"
                      onClick={(e) => { e.stopPropagation(); setReturnDialog(true); }}
                      sx={{ 
                        borderColor: '#ff9800', 
                        color: '#ff9800', 
                        fontSize: 11, 
                        fontWeight: 600,
                        '&:hover': { background: '#fff3e0', borderColor: '#ff9800' } 
                      }}
                    >
                      Return Vehicle
                    </Button>
                    <Button
                      fullWidth
                      size="small"
                      variant="outlined"
                      onClick={(e) => { e.stopPropagation(); openChangeVehicleDialog(); }}
                      sx={{ 
                        borderColor: '#2196f3', 
                        color: '#2196f3', 
                        fontSize: 11, 
                        fontWeight: 600,
                        '&:hover': { background: '#e3f2fd', borderColor: '#2196f3' } 
                      }}
                    >
                      Change Vehicle
                    </Button>
                  </Box>
                </>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Action Buttons */}
              <Button
                fullWidth
                variant="contained"
                startIcon={<LocationOn />}
                onClick={openMapDialog}
                sx={{ borderRadius: 2, background: 'linear-gradient(135deg,#2e7d32,#388e3c)', fontSize: 12, fontWeight: 600, mb: 1 }}
              >
                View on Map
              </Button>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Edit />}
                onClick={() => setActiveTab('Edit')}
                sx={{ borderRadius: 2, color: '#1a237e', borderColor: '#1a237e', fontSize: 12, fontWeight: 600, mb: 1, '&:hover': { background: '#e8eaf6' } }}
              >
                Edit User
              </Button>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Delete />}
                onClick={handleDelete}
                sx={{ borderRadius: 2, color: '#c62828', borderColor: '#c62828', fontSize: 12, fontWeight: 600, '&:hover': { background: '#ffebee' } }}
              >
                Delete User
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Content (9 cols) */}
        <Grid item xs={12} md={9}>
          
          {/* Pill-style Tabs */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            {TABS.map((t) => (
              <Box
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.8,
                  px: 2,
                  py: 1,
                  borderRadius: 10,
                  cursor: 'pointer',
                  border: '1.5px solid',
                  borderColor: activeTab === t.id ? '#1a237e' : '#e8eaf6',
                  background: activeTab === t.id ? '#e8eaf6' : '#fff',
                  transition: 'all 0.2s',
                }}
              >
                <Typography fontSize={13} fontWeight={700} color={activeTab === t.id ? '#1a237e' : 'text.secondary'}>
                  {t.label}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Tab Content */}
          <Card sx={{ borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
            <CardContent sx={{ p: 3 }}>
              
              {/* User Information Tab */}
              {activeTab === 'User' && (
                <>
                  <Typography variant="h6" fontWeight={700} color="#1a237e" mb={2}>User Information</Typography>
                  <Grid container spacing={0}>
                    {[
                      { label: 'Full Name', value: user.name },
                      { label: 'Phone', value: user.phone },
                      { label: 'Email', value: user.email },
                      { label: 'Address', value: user.address },
                      { label: 'KYC Status', value: user.kyc_status?.toUpperCase() },
                      { label: 'Status', value: user.is_active ? 'Active' : 'Inactive' },
                      { label: 'Registered', value: formatDate(user.created_at) },
                      { label: 'Last Updated', value: formatDate(user.updated_at) },
                    ].map(({ label, value }) => (
                      <Grid item xs={12} sm={6} key={label} sx={{ px: 1 }}>
                        <InfoRow label={label} value={value} />
                      </Grid>
                    ))}
                  </Grid>
                </>
              )}

              {/* Vehicle History Tab */}
              {activeTab === 'History' && (
                <>
                  <Typography variant="h6" fontWeight={700} color="#1a237e" mb={2}>Vehicle Assignment History</Typography>

                  {historyLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                      <CircularProgress sx={{ color: '#1a237e' }} />
                    </Box>
                  ) : history.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 8, background: '#f8fafc', borderRadius: 2 }}>
                      <Typography color="text.secondary" fontWeight={600}>No assignment history found</Typography>
                      <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                        Vehicle assignments and returns will appear here
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ position: 'relative' }}>
                      {/* Timeline line */}
                      <Box sx={{ position: 'absolute', left: 19, top: 0, bottom: 0, width: 2, background: '#e8eaf6' }} />

                      {history.map((record, i) => {
                        const isAssigned = record.event_type === 'assigned';
                        const dotColor = isAssigned ? '#16a34a' : '#f59e0b';
                        const bgColor  = isAssigned ? '#f0fdf4'  : '#fffbeb';

                        return (
                          <Box key={i} sx={{ display: 'flex', gap: 2, mb: 3, position: 'relative' }}>
                            {/* Timeline dot */}
                            <Box sx={{
                              width: 40, height: 40, borderRadius: '50%',
                              background: bgColor, border: `3px solid ${dotColor}`,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              flexShrink: 0, zIndex: 1,
                            }}>
                              {isAssigned
                                ? <Assignment sx={{ fontSize: 18, color: dotColor }} />
                                : <AssignmentReturn sx={{ fontSize: 18, color: dotColor }} />
                              }
                            </Box>

                            {/* Card */}
                            <Card sx={{ flex: 1, p: 2.5, border: `1px solid ${dotColor}40`, borderRadius: 2, background: bgColor }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                <Typography fontSize={14} fontWeight={700} color={dotColor}>
                                  {isAssigned ? 'Vehicle Assigned' : 'Vehicle Returned'}
                                </Typography>
                                <Chip
                                  label={`${formatDate(record.date)}${record.time ? ` at ${record.time}` : ''}`}
                                  size="small"
                                  sx={{ background: '#fff', color: dotColor, fontSize: 10, fontWeight: 600, height: 22, border: `1px solid ${dotColor}40` }}
                                />
                              </Box>

                              <Grid container spacing={1.5}>
                                <Grid item xs={6}>
                                  <Typography fontSize={10} color="text.secondary" fontWeight={600}>Vehicle No.</Typography>
                                  <Typography fontSize={13} fontWeight={700} sx={{ fontFamily: 'monospace' }}>
                                    {record.vehicle_number || '—'}
                                  </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography fontSize={10} color="text.secondary" fontWeight={600}>Model</Typography>
                                  <Typography fontSize={13} fontWeight={600}>{record.vehicle_model || '—'}</Typography>
                                </Grid>
                                <Grid item xs={12}>
                                  <Typography fontSize={10} color="text.secondary" fontWeight={600}>
                                    {isAssigned ? 'Assigned By' : 'Collected By'}
                                  </Typography>
                                  <Typography fontSize={13} fontWeight={600}>{record.by_name || '—'}</Typography>
                                </Grid>
                                {!isAssigned && record.notes && (
                                  <Grid item xs={12}>
                                    <Typography fontSize={10} color="text.secondary" fontWeight={600}>Return Notes</Typography>
                                    <Typography fontSize={12} sx={{ mt: 0.5, p: 1, background: '#fff', borderRadius: 1, border: `1px solid ${dotColor}40` }}>
                                      {record.notes}
                                    </Typography>
                                  </Grid>
                                )}
                              </Grid>
                            </Card>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </>
              )}

              {/* Call Logs Tab - Combined with Search */}
              {activeTab === 'CallLogs' && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" fontWeight={700} color="#1a237e">Call Logs</Typography>
                    <TextField
                      size="small"
                      placeholder="Search number or name..."
                      value={callSearch}
                      onChange={(e) => {
                        setCallSearch(e.target.value);
                        setCallPage(0);
                      }}
                      sx={{ minWidth: 250 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                  
                  {callsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                      <CircularProgress sx={{ color: '#1a237e' }} />
                    </Box>
                  ) : (() => {
                    const filteredCalls = callRecords.filter(call =>
                      !callSearch ||
                      call.number?.toLowerCase().includes(callSearch.toLowerCase()) ||
                      call.name?.toLowerCase().includes(callSearch.toLowerCase())
                    );
                    
                    const paginatedCalls = filteredCalls.slice(
                      callPage * callPageSize,
                      callPage * callPageSize + callPageSize
                    );
                    
                    return filteredCalls.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 8, background: '#f8fafc', borderRadius: 2 }}>
                        <Call sx={{ fontSize: 56, color: '#e8eaf6', mb: 1 }} />
                        <Typography color="text.secondary" fontWeight={600}>
                          {callSearch ? 'No matching call logs found' : 'No call logs found'}
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          Showing {callPage * callPageSize + 1}-{Math.min((callPage + 1) * callPageSize, filteredCalls.length)} of {filteredCalls.length} call logs
                        </Typography>
                        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e8eaf6', borderRadius: 2 }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={{ background: '#1a237e' }}>
                                <TableCell sx={{ fontWeight: 800, color: '#fff' }}>Number</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#fff' }}>Name</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#fff' }}>Type</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#fff' }}>Duration</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#fff' }}>Date & Time</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {paginatedCalls.map((call, i) => (
                                <TableRow key={i} hover>
                                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{call.number || '—'}</TableCell>
                                  <TableCell sx={{ fontSize: 12 }}>{call.name || '—'}</TableCell>
                                  <TableCell>
                                    <Chip 
                                      label={call.type || '—'} 
                                      size="small" 
                                      sx={{ 
                                        fontSize: 10, 
                                        background: call.type === 'incoming' || call.type === 'INCOMING' ? '#e3f2fd' : 
                                                   call.type === 'outgoing' || call.type === 'OUTGOING' ? '#e8f5e9' : '#ffebee',
                                        color: call.type === 'incoming' || call.type === 'INCOMING' ? '#0277bd' : 
                                               call.type === 'outgoing' || call.type === 'OUTGOING' ? '#2e7d32' : '#c62828',
                                        fontWeight: 600
                                      }} 
                                    />
                                  </TableCell>
                                  <TableCell sx={{ fontSize: 12 }}>
                                    {call.duration ? (
                                      typeof call.duration === 'number' && call.duration > 60 
                                        ? `${Math.floor(call.duration / 60)}m ${call.duration % 60}s` 
                                        : `${call.duration}s`
                                    ) : '—'}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: 12 }}>
                                    {call.date || '—'}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        <TablePagination
                          component="div"
                          count={filteredCalls.length}
                          page={callPage}
                          onPageChange={(_, newPage) => setCallPage(newPage)}
                          rowsPerPage={callPageSize}
                          onRowsPerPageChange={(e) => {
                            setCallPageSize(parseInt(e.target.value, 10));
                            setCallPage(0);
                          }}
                          rowsPerPageOptions={[10, 25, 50, 100]}
                          sx={{ borderTop: '1px solid #e8eaf6' }}
                        />
                      </>
                    );
                  })()}
                </>
              )}

              {/* Contacts Tab - Table with Search */}
              {activeTab === 'Contacts' && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" fontWeight={700} color="#1a237e">Contacts</Typography>
                    <TextField
                      size="small"
                      placeholder="Search name or phone..."
                      value={contactSearch}
                      onChange={(e) => {
                        setContactSearch(e.target.value);
                        setContactPage(0);
                      }}
                      sx={{ minWidth: 250 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                  
                  {contactsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                      <CircularProgress sx={{ color: '#1a237e' }} />
                    </Box>
                  ) : (() => {
                    const filteredContacts = contacts.filter(contact =>
                      !contactSearch ||
                      contact.name?.toLowerCase().includes(contactSearch.toLowerCase()) ||
                      (contact.phones ?? []).some(p => p.includes(contactSearch))
                    );
                    
                    const paginatedContacts = filteredContacts.slice(
                      contactPage * contactPageSize,
                      contactPage * contactPageSize + contactPageSize
                    );
                    
                    return filteredContacts.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 8, background: '#f8fafc', borderRadius: 2 }}>
                        <ContactsIcon sx={{ fontSize: 56, color: '#e8eaf6', mb: 1 }} />
                        <Typography color="text.secondary" fontWeight={600}>
                          {contactSearch ? 'No matching contacts found' : 'No contacts found'}
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          Showing {contactPage * contactPageSize + 1}-{Math.min((contactPage + 1) * contactPageSize, filteredContacts.length)} of {filteredContacts.length} contacts
                        </Typography>
                        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e8eaf6', borderRadius: 2 }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={{ background: '#1a237e' }}>
                                <TableCell sx={{ fontWeight: 800, color: '#fff' }}>Name</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#fff' }}>Phone Numbers</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#fff' }}>Emails</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {paginatedContacts.map((contact, i) => (
                                <TableRow key={i} hover>
                                  <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>{contact.name || '—'}</TableCell>
                                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>
                                    {(contact.phones ?? []).length > 0 ? (
                                      contact.phones.map((phone, idx) => (
                                        <Box key={idx} component="span" sx={{ display: 'block' }}>{phone}</Box>
                                      ))
                                    ) : '—'}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: 11 }}>
                                    {(contact.emails ?? []).length > 0 ? (
                                      contact.emails.map((email, idx) => (
                                        <Box key={idx} component="span" sx={{ display: 'block' }}>{email}</Box>
                                      ))
                                    ) : '—'}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        <TablePagination
                          component="div"
                          count={filteredContacts.length}
                          page={contactPage}
                          onPageChange={(_, newPage) => setContactPage(newPage)}
                          rowsPerPage={contactPageSize}
                          onRowsPerPageChange={(e) => {
                            setContactPageSize(parseInt(e.target.value, 10));
                            setContactPage(0);
                          }}
                          rowsPerPageOptions={[10, 25, 50, 100]}
                          sx={{ borderTop: '1px solid #e8eaf6' }}
                        />
                      </>
                    );
                  })()}
                </>
              )}

              {/* Photos Tab */}
              {activeTab === 'Photos' && (
                <>
                  <Typography variant="h6" fontWeight={700} color="#1a237e" mb={2}>Photos</Typography>
                  
                  {photosLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                      <CircularProgress sx={{ color: '#1a237e' }} />
                    </Box>
                  ) : photos.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 8, background: '#f8fafc', borderRadius: 2 }}>
                      <PhotoLibrary sx={{ fontSize: 56, color: '#e8eaf6', mb: 1 }} />
                      <Typography color="text.secondary" fontWeight={600}>No photos found</Typography>
                    </Box>
                  ) : (
                    <Grid container spacing={2}>
                      {photos.map((photo, i) => (
                        <Grid item xs={6} sm={4} md={3} key={i}>
                          <Box
                            component="img"
                            src={`${Config.base_url}/uploads/device-photos/${userId?.replace(/[^a-zA-Z0-9_-]/g, '_')}/${photo.filename}`}
                            alt={`Photo ${i + 1}`}
                            sx={{
                              width: '100%',
                              height: 150,
                              objectFit: 'cover',
                              borderRadius: 2,
                              cursor: 'pointer',
                              '&:hover': { transform: 'scale(1.05)', transition: 'all 0.2s' }
                            }}
                            onClick={() => window.open(`${Config.base_url}/uploads/device-photos/${userId?.replace(/[^a-zA-Z0-9_-]/g, '_')}/${photo.filename}`, '_blank')}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </>
              )}

              {/* Payments Tab */}
              {activeTab === 'Payments' && (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" fontWeight={700} color="#1a237e">Payment Plans</Typography>
                    <Button variant="contained" startIcon={<Add />} onClick={openAddPlan}
                      sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700, fontSize: 12 }}>
                      Add Plan
                    </Button>
                  </Box>

                  {plansLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                      <CircularProgress sx={{ color: '#1a237e' }} />
                    </Box>
                  ) : plans.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 6, color: '#94a3b8' }}>
                      <Payment sx={{ fontSize: 48, mb: 1, opacity: 0.4 }} />
                      <Typography>No payment plans yet</Typography>
                      <Typography variant="caption">Click "Add Plan" to create one</Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {plans.map(plan => {
                        const isOpen    = expandedPlan === plan.id;
                        const cols      = collections[plan.id] || [];
                        const pending   = cols.filter(c => c.status === 'pending' || c.status === 'partial').length;
                        const overdue   = cols.filter(c => c.status === 'overdue').length;
                        return (
                          <Card key={plan.id} elevation={0} sx={{ border: overdue > 0 ? '1.5px solid #fca5a5' : '1px solid #e2e8f0', borderRadius: 2 }}>
                            <CardContent sx={{ p: 2, pb: '12px !important' }}>
                              {/* Plan header */}
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
                                <Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <Chip label={FREQ_LABEL[plan.frequency] || plan.frequency} size="small"
                                      sx={{ background: '#e8eaf6', color: '#1a237e', fontWeight: 700, fontSize: 11 }} />
                                    {overdue > 0 && <Chip label={`${overdue} Overdue`} size="small" color="error" />}
                                    {pending > 0 && <Chip label={`${pending} Pending`} size="small" color="warning" />}
                                  </Box>
                                  <Typography fontWeight={800} fontSize={20} color="#1a237e">
                                    ₹{parseFloat(plan.amount).toLocaleString('en-IN')}
                                    <Typography component="span" fontSize={12} color="text.secondary" fontWeight={400}> / {plan.frequency}</Typography>
                                  </Typography>
                                  {parseFloat(plan.advance_paid) > 0 && (
                                    <Typography fontSize={12} color="#16a34a">
                                      Advance: ₹{parseFloat(plan.advance_paid).toLocaleString('en-IN')}
                                    </Typography>
                                  )}
                                  <Typography fontSize={11} color="text.secondary" mt={0.3}>
                                    From {plan.start_date} · Collected: ₹{parseFloat(plan.total_collected || 0).toLocaleString('en-IN')}
                                    {plan.total_collections > 0 && ` (${plan.total_collections} entries)`}
                                  </Typography>
                                  {plan.notes && <Typography fontSize={11} color="text.secondary" sx={{ mt: 0.3, fontStyle: 'italic' }}>{plan.notes}</Typography>}
                                </Box>
                                <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                                  <Tooltip title="Send Reminder">
                                    <IconButton size="small" onClick={() => openReminder(plan)} sx={{ color: '#f59e0b', border: '1px solid #fde68a', borderRadius: 1.5 }}>
                                      <NotificationsActive fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Add Collection">
                                    <IconButton size="small" onClick={() => openAddCol(plan.id)} sx={{ color: '#16a34a', border: '1px solid #bbf7d0', borderRadius: 1.5 }}>
                                      <Add fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Edit Plan">
                                    <IconButton size="small" onClick={() => openEditPlan(plan)} sx={{ color: '#1565c0', border: '1px solid #bfdbfe', borderRadius: 1.5 }}>
                                      <Edit fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Button size="small" onClick={() => togglePlan(plan.id)}
                                    sx={{ fontSize: 11, color: '#64748b', minWidth: 60 }}>
                                    {isOpen ? 'Hide' : 'History'}
                                  </Button>
                                </Box>
                              </Box>

                              {/* Collections table */}
                              {isOpen && (
                                <Box sx={{ mt: 2 }}>
                                  <Divider sx={{ mb: 1.5 }} />
                                  {cols.length === 0 ? (
                                    <Typography variant="caption" color="text.secondary">No collections recorded yet.</Typography>
                                  ) : (
                                    <TableContainer>
                                      <Table size="small">
                                        <TableHead>
                                          <TableRow sx={{ background: '#f8fafc' }}>
                                            {['Date', 'Due Date', 'Amount', 'Method', 'Status', 'Type', 'Collected By', 'Notes', ''].map(h => (
                                              <TableCell key={h} sx={{ fontWeight: 700, fontSize: 11, color: '#64748b', py: 0.8, whiteSpace: 'nowrap' }}>{h}</TableCell>
                                            ))}
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {cols.map(col => (
                                            <TableRow key={col.id} hover>
                                              <TableCell sx={{ fontSize: 12 }}>{col.paid_date || '—'}</TableCell>
                                              <TableCell sx={{ fontSize: 12, color: col.due_date && new Date(col.due_date) < new Date() && col.status !== 'paid' ? '#dc2626' : 'inherit' }}>
                                                {col.due_date || '—'}
                                              </TableCell>
                                              <TableCell sx={{ fontSize: 13, fontWeight: 700, color: '#1a237e' }}>
                                                ₹{parseFloat(col.amount).toLocaleString('en-IN')}
                                              </TableCell>
                                              <TableCell sx={{ fontSize: 11 }}>{METHOD_LABEL[col.method] || col.method}</TableCell>
                                              <TableCell>
                                                <Chip label={col.status} size="small" color={STATUS_COLOR[col.status] || 'default'}
                                                  sx={{ fontWeight: 700, fontSize: 10, textTransform: 'capitalize' }} />
                                              </TableCell>
                                              <TableCell>
                                                <Chip
                                                  label={{rent:'Rent',advance:'Advance',fine:'Fine',other:'Other'}[col.transaction_type] || col.transaction_type || 'Rent'}
                                                  size="small"
                                                  sx={{
                                                    fontSize: 10, fontWeight: 600, height: 20,
                                                    bgcolor: {rent:'#eff6ff',advance:'#fefce8',fine:'#fef2f2',other:'#f8fafc'}[col.transaction_type] || '#f8fafc',
                                                    color:   {rent:'#1d4ed8',advance:'#a16207',fine:'#dc2626',other:'#475569'}[col.transaction_type] || '#475569',
                                                  }}
                                                />
                                              </TableCell>
                                              <TableCell sx={{ fontSize: 11 }}>{col.collected_by_name || '—'}</TableCell>
                                              <TableCell sx={{ fontSize: 11, maxWidth: 120 }}>{col.notes || '—'}</TableCell>
                                              <TableCell>
                                                <Tooltip title="Edit">
                                                  <IconButton size="small" onClick={() => openEditCol(plan.id, col)} sx={{ color: '#1565c0' }}>
                                                    <Edit sx={{ fontSize: 14 }} />
                                                  </IconButton>
                                                </Tooltip>
                                              </TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    </TableContainer>
                                  )}
                                </Box>
                              )}
                            </CardContent>
                          </Card>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              )}

              {/* Edit Tab */}
              {activeTab === 'Edit' && (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6" fontWeight={700} color="#1a237e">Edit User</Typography>
                    <Button
                      variant="contained"
                      startIcon={<Edit />}
                      onClick={handleSave}
                      disabled={saving}
                      sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', fontSize: 12, fontWeight: 600 }}
                    >
                      {saving ? <CircularProgress size={18} color="inherit" /> : 'Save Changes'}
                    </Button>
                  </Box>

                  <Grid container spacing={2.5}>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth size="small" label="Name *" value={form.name} onChange={f('name')} required />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth size="small" label="Phone *" value={form.phone} onChange={f('phone')} required />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth size="small" label="Email" type="email" value={form.email} onChange={f('email')} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth size="small" select label="KYC Status" value={form.kyc_status} onChange={f('kyc_status')}>
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="approved">Approved</MenuItem>
                        <MenuItem value="rejected">Rejected</MenuItem>
                        <MenuItem value="review">In Review</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth size="small" label="Address" value={form.address} onChange={f('address')} multiline rows={2} />
                    </Grid>
                  </Grid>
                </>
              )}

            </CardContent>
          </Card>

        </Grid>

      </Grid>

      {/* GPS Map Dialog */}
      <Dialog open={mapDialog} onClose={() => setMapDialog(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 1 } }}>
        <DialogTitle sx={{ fontWeight: 800, color: '#1a237e', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocationOn />
            <span>User Location</span>
          </Box>
          <IconButton size="small" onClick={() => setMapDialog(false)}><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ height: 480 }}>
            {gpsData?.latitude ? (
              <MapContainer
                center={[gpsData.latitude, gpsData.longitude]}
                zoom={15}
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom={true}
              >
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="© OpenStreetMap" />
                <Marker position={[gpsData.latitude, gpsData.longitude]}>
                  <Popup>
                    <strong>{user.name}</strong><br />
                    {parseFloat(gpsData.latitude)?.toFixed(6)}, {parseFloat(gpsData.longitude)?.toFixed(6)}
                  </Popup>
                </Marker>
              </MapContainer>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', background: '#f8fafc' }}>
                <Typography color="text.secondary">No GPS location available</Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setMapDialog(false)} sx={{ fontWeight: 700 }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Return Vehicle Dialog */}
      <Dialog open={returnDialog} onClose={() => setReturnDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, color: '#1a237e', pb: 1 }}>
          Return Vehicle
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Return <strong>{user.vehicle?.vehicle_number}</strong> from <strong>{user.name}</strong>?
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Return Notes (Optional)"
            placeholder="Enter any notes about the vehicle condition or return details..."
            value={returnNotes}
            onChange={(e) => setReturnNotes(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setReturnDialog(false)} disabled={returningVehicle}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleReturnVehicle}
            disabled={returningVehicle}
            sx={{ background: 'linear-gradient(135deg,#ff9800,#f57c00)', fontWeight: 600 }}
          >
            {returningVehicle ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Return Vehicle'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Vehicle Dialog */}
      <Dialog open={changeDialog} onClose={() => setChangeDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, color: '#1a237e', pb: 1 }}>
          Change Vehicle
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Current: <strong>{user.vehicle?.vehicle_number}</strong>
          </Typography>
          <Typography variant="body2" fontWeight={600} mb={1}>
            Select New Vehicle:
          </Typography>
          <TextField
            select
            fullWidth
            size="small"
            value={selectedNewVehicle}
            onChange={(e) => setSelectedNewVehicle(e.target.value)}
            label="Available Vehicles"
            helperText={availableVehicles.length === 0 ? 'No unassigned vehicles available' : `${availableVehicles.length} vehicles available`}
          >
            {availableVehicles.length === 0 ? (
              <MenuItem disabled>No vehicles available</MenuItem>
            ) : (
              availableVehicles.map((v) => (
                <MenuItem key={v.id} value={v.id}>
                  {v.vehicle_number} - {v.model} ({v.color})
                </MenuItem>
              ))
            )}
          </TextField>
          <Alert severity="info" sx={{ mt: 2, fontSize: 12 }}>
            This will return the current vehicle and assign the new one in a single operation.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setChangeDialog(false)} disabled={changingVehicle}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleChangeVehicle}
            disabled={changingVehicle || !selectedNewVehicle}
            sx={{ background: 'linear-gradient(135deg,#2196f3,#1976d2)', fontWeight: 600 }}
          >
            {changingVehicle ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Change Vehicle'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnack({ ...snack, open: false })} severity={snack.sev} sx={{ width: '100%' }}>
          {snack.msg}
        </Alert>
      </Snackbar>

      {/* ── Plan Dialog ──────────────────────────────────────────────────────── */}
      <Dialog open={planDialog} onClose={() => setPlanDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {editPlan ? 'Edit Payment Plan' : 'Add Payment Plan'}
          <IconButton size="small" onClick={() => setPlanDialog(false)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" select label="Frequency *" value={planForm.frequency}
                onChange={e => setPlanForm(p => ({ ...p, frequency: e.target.value }))}>
                <MenuItem value="daily">Daily</MenuItem>
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Amount (₹) *" type="number" value={planForm.amount}
                onChange={e => setPlanForm(p => ({ ...p, amount: e.target.value }))}
                inputProps={{ min: 1 }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Advance Paid (₹)" type="number" value={planForm.advance_paid}
                onChange={e => setPlanForm(p => ({ ...p, advance_paid: e.target.value }))}
                inputProps={{ min: 0 }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Start Date *" type="date" value={planForm.start_date}
                onChange={e => setPlanForm(p => ({ ...p, start_date: e.target.value }))}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Notes" multiline rows={2} value={planForm.notes}
                onChange={e => setPlanForm(p => ({ ...p, notes: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setPlanDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSavePlan} disabled={savingPlan}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', fontWeight: 700, minWidth: 100 }}>
            {savingPlan ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Collection Dialog ────────────────────────────────────────────────── */}
      <Dialog open={colDialog} onClose={() => setColDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {editCol ? 'Edit Collection' : 'Record Payment'}
          <IconButton size="small" onClick={() => setColDialog(false)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Amount (₹) *" type="number" value={colForm.amount}
                onChange={e => setColForm(p => ({ ...p, amount: e.target.value }))}
                inputProps={{ min: 1 }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" select label="Transaction Type *" value={colForm.transaction_type}
                onChange={e => setColForm(p => ({ ...p, transaction_type: e.target.value }))}>
                <MenuItem value="rent">🔁 Rent</MenuItem>
                <MenuItem value="advance">💰 Advance</MenuItem>
                <MenuItem value="fine">⚠️ Fine</MenuItem>
                <MenuItem value="other">📋 Other</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" select label="Status *" value={colForm.status}
                onChange={e => setColForm(p => ({ ...p, status: e.target.value }))}>
                <MenuItem value="paid">Paid</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="partial">Partial</MenuItem>
                <MenuItem value="overdue">Overdue</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Paid Date" type="date" value={colForm.paid_date}
                onChange={e => setColForm(p => ({ ...p, paid_date: e.target.value }))}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Due Date" type="date" value={colForm.due_date}
                onChange={e => setColForm(p => ({ ...p, due_date: e.target.value }))}
                InputLabelProps={{ shrink: true }} helperText="Leave blank if not applicable" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" select label="Payment Method" value={colForm.method}
                onChange={e => setColForm(p => ({ ...p, method: e.target.value }))}>
                <MenuItem value="cash">💵 Cash</MenuItem>
                <MenuItem value="upi">📱 UPI</MenuItem>
                <MenuItem value="bank">🏦 Bank Transfer</MenuItem>
                <MenuItem value="other">📋 Other</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Notes" value={colForm.notes}
                onChange={e => setColForm(p => ({ ...p, notes: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setColDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveCol} disabled={savingCol}
            sx={{ background: 'linear-gradient(135deg,#16a34a,#15803d)', fontWeight: 700, minWidth: 120 }}>
            {savingCol ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : editCol ? 'Update' : 'Record Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Reminder Dialog ──────────────────────────────────────────────────── */}
      <Dialog open={reminderDialog} onClose={() => setReminderDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Send Payment Reminder
          <IconButton size="small" onClick={() => setReminderDialog(false)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          {reminderPlan && (
            <Box sx={{ mb: 2, p: 1.5, background: '#f0f9ff', borderRadius: 1.5, border: '1px solid #bae6fd' }}>
              <Typography fontSize={12} color="text.secondary">Plan</Typography>
              <Typography fontWeight={700}>₹{parseFloat(reminderPlan.amount).toLocaleString('en-IN')} / {reminderPlan.frequency}</Typography>
            </Box>
          )}
          <TextField
            fullWidth multiline rows={3}
            label="Custom Message (optional)"
            placeholder="Leave blank to use the default reminder message..."
            value={reminderMsg}
            onChange={e => setReminderMsg(e.target.value)}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            A push notification will be sent to the user's device.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setReminderDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSendReminder} disabled={sendingReminder}
            startIcon={<NotificationsActive />}
            sx={{ background: 'linear-gradient(135deg,#f59e0b,#d97706)', fontWeight: 700, minWidth: 140 }}>
            {sendingReminder ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Send Reminder'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default UserDetail;
